export enum Size {
  mobileS = 320,
  mobileM = 375,
  mobileL = 425,
  mobileXL = 600,
  tablet = 768,
  laptopS = 800,
  laptopMS = 906,
  laptop = 1024,
  laptopM = 1300,
  laptopL = 1440,
  desktop = 2560,
  desktopL = 2600,
}

const getSizeQuery = ({ isMaxQuery = false, size }: { size: Size; isMaxQuery?: boolean }) =>
  `(${isMaxQuery ? 'max' : 'min'}-width: ${size}px)`;

export const defaultTheme = {
  font: {
    family: '"Poppins", sans-serif;',
    size: {
      smaller: '10px',
      small: '14px',
      smallMedium: '16px',
      smallMedium2: '18px',
      smallMedium2Plus1: '19px',
      smallMedium3: '20px',
      medium: '22px',
      medium2: '24px',
      medium3: '26px',
      mediumLarge: '30px',
      large: '36px',
      huge: '42px',
      huger: '48px',
      gargantuan: '100px',
    },
    weight: {
      lighterEx: 100,
      lighter: 200,
      light: 300,
      normal: 400,
      medium: 500,
      bold: 600,
      bolder: 700,
      boldest: 900,
    },
  },
  colors: {
    white: '#fff',
    whiteGray: '#f4f4f4',
    black: '#000',
    light: '#38c5f3',
    light2: '#41c6f2',
    light3: '#00a7e1',
    light4: '#04a6e1',
    light5: '#03a5df',
    dark: '#345275',
    border: '#e0e0e0',
    seperator: '#c0c0c0',
    divider: '#E7E9EE',
    grayBlueish: '#77888c',
    gray: '#9e9e9e',
    mediumGray: '#8F97A8',
    grayWhite: '#F1F3F5',
    grayLight: '#f7f7f7',
    grayLighter: '#E2E8F0',
    grayLight2: '#829099',
    grayDark: 'rgb(74,74,74)',
    primaryDark: '#345374',
    primaryDark2: '#212d65',
    heroBackground: '#FCFCFC',
    darkOpac: 'rgba(52,82,117, 0.6)',
    error: '#f56161',
    error2: '#c8203d',
    error3: '#c8564b',
    error4: '#ED726B',
    warning: '#f5a623',
    disabledBG: '#E7E9EE',
    disabledText: '#BCC1CB',
    extraLight: '#E7E9EE',
    disabledButton: '#d8d8d8',
    inputBackground: 'rgba(54, 54, 54, 0.05)',
    tabsBackGround: 'rgba(133,149,159, 0.07)',
    tableHeaderBackground: 'rgba(133,149,159, 0.1)',
    tableBorderBackground: 'rgba(133,149,159, 0.5)',
    placeholder: '#6d6e71',
    placeholder2: '#8090B4',
    placeholderGray: '#c2c2c2',
    sunbitYellow: '#ffc80a',
    headlineGrey: '#8597ad',
    headlineGrey2: '#7897ab',
    headlineGrey3: '#9da6ac',
    headlineGrey4: '#85959f',
    homeBackground: '#f3f9ff',
    inputBorder: '#d1d9e0',
    borderShadow: 'rgba(24, 39, 75, 0.12)',
    primary: '#3156DB',
    primaryBackground: '#002169',
    accent: '#FFD179',
    secondaryOrange: '#FF9E4E',
    secondaryIceBlue: '#E6FBFE',
    secondaryDarkGray: '#4A5568',
    desktopBackground: '#FBFCFF',
    darkGray: '#616672',
    primaryBright: '#3156FF',
    lightBackground: '#F6F9FC',
    bulletBackground: 'linear-gradient(0deg, rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.85)), #3156FF',
    lightGray: '#A9B5C8',
    lightBlue: '#03a5df',
    lighterBlue: '#F5F7FF',
    primaryDeepBlue: '#002169',
    primaryLightBlue: '#D5F2FF',
    tintDeepBlue70: '#B2BCD2',
    tintDeepBlue95: '#F2F4F7',
    tintDeepBlue35: '#596F9D',
    lightGreen2: '#67B755',
  },
  desktop: {
    width: 1330,
    height: 780,
  },
  pos: {
    width: 768,
    height: 1024,
  },
  spacing: {
    small: 5,
    smallMedium: 7,
    smallMedium2: 8,
    medium: 10,
    mediumLarger: 15,
    large: 20,
    large2: 25,
    larger: 30,
    larger2: 40,
    huge: 60,
    hugeHuger: 75,
    huger: 90,
    smallerGargantuan: 110,
    gargantuan: 120,
    mobile: 27,
  },
  device: {
    mobileS: getSizeQuery({ size: Size.mobileS }),
    mobileM: getSizeQuery({ size: Size.mobileM }),
    mobileL: getSizeQuery({ size: Size.mobileL }),
    mobileXL: getSizeQuery({ size: Size.mobileXL }),
    tablet: getSizeQuery({ size: Size.tablet }),
    laptopS: getSizeQuery({ size: Size.laptopS }),
    laptop: getSizeQuery({ size: Size.laptop }),
    laptopL: getSizeQuery({ size: Size.laptopL }),
    desktop: getSizeQuery({ size: Size.desktop }),
    desktopL: getSizeQuery({ size: Size.desktopL }),
    laptopMS: getSizeQuery({ size: Size.laptopMS }),
    laptopM: getSizeQuery({ size: Size.laptopM }),
    max: {
      mobileS: getSizeQuery({ size: Size.mobileS, isMaxQuery: true }),
      mobileM: getSizeQuery({ size: Size.mobileM, isMaxQuery: true }),
      mobileL: getSizeQuery({ size: Size.mobileL, isMaxQuery: true }),
      mobileXL: getSizeQuery({ size: Size.mobileXL, isMaxQuery: true }),
      tablet: getSizeQuery({ size: Size.tablet, isMaxQuery: true }),
      laptopS: getSizeQuery({ size: Size.laptopS, isMaxQuery: true }),
      laptop: getSizeQuery({ size: Size.laptop, isMaxQuery: true }),
      laptopL: getSizeQuery({ size: Size.laptopL, isMaxQuery: true }),
      desktop: getSizeQuery({ size: Size.desktop, isMaxQuery: true }),
      desktopL: getSizeQuery({ size: Size.desktopL, isMaxQuery: true }),
      laptopMS: getSizeQuery({ size: Size.laptopMS, isMaxQuery: true }),
      laptopM: getSizeQuery({ size: Size.laptopM, isMaxQuery: true }),
    },
  },
  borderRadiusCommon: 8,
  borderRadiusMedium: 16,
  borderRadiusButton: {
    small: 24,
    medium: 28,
    mediumLarge: 31.2,
    large: 35.5,
  },
  borderRadiusRound: 2000,
  icons: {
    sizes: {
      iconSizeMicro: '6',
      iconSizeTiny: '10',
      iconSizeSmall: '20',
      iconSizeNormal: '24',
      iconSizeMedium: '40',
      iconSizeLarge: '50',
    },
    symbols: {
      materialIconArrowForward: 'arrow_forward',
      materialIconStore: 'store',
      materialIconApply: 'touch_app',
      materialIconShieldConfirm: 'verified_user',
      materialIconCircleFull: 'lens',
    },
  },
};

export type DefaultThemeType = typeof defaultTheme;
